import generatedCatalog from "./officerCatalog.generated.json";

export const OFFICER_CATEGORY_ORDER = Array.isArray(generatedCatalog?.categories)
  ? generatedCatalog.categories.map((entry) => String(entry?.key || "").trim()).filter(Boolean)
  : ["director", "strategy", "machinist", "drillmaster", "warfare"];

const OFFICER_CATEGORY_LABELS = (
  Array.isArray(generatedCatalog?.categories) ? generatedCatalog.categories : []
).reduce((acc, entry = {}) => {
  const key = String(entry?.key || "").trim();
  if (!key) return acc;
  acc[key] = String(entry?.label || key);
  return acc;
}, {});

export const OFFICER_APPOINTMENT_LIMITS = generatedCatalog?.appointmentLimits && typeof generatedCatalog.appointmentLimits === "object"
  ? generatedCatalog.appointmentLimits
  : {};

const OFFICER_CONSTRAINTS = generatedCatalog?.constraints && typeof generatedCatalog.constraints === "object"
  ? generatedCatalog.constraints
  : {};

const OFFICER_MASTERY_TABLES = generatedCatalog?.mastery?.levelValueTables && typeof generatedCatalog.mastery.levelValueTables === "object"
  ? generatedCatalog.mastery.levelValueTables
  : {};
const OFFICER_LEVEL_EXP_TABLES = generatedCatalog?.mastery?.levelExpTables && typeof generatedCatalog.mastery.levelExpTables === "object"
  ? generatedCatalog.mastery.levelExpTables
  : {};
const OFFICER_SKILL_LEVEL_EXP_TABLE = Array.isArray(generatedCatalog?.skillLevelExpTable)
  ? generatedCatalog.skillLevelExpTable
  : [];
const RESOURCE_CATALOG_RAW = generatedCatalog?.resourceCatalog && typeof generatedCatalog.resourceCatalog === "object"
  ? generatedCatalog.resourceCatalog
  : {};

const CATEGORY_POOL_BY_KEY = generatedCatalog?.skillPoolsByCategory && typeof generatedCatalog.skillPoolsByCategory === "object"
  ? generatedCatalog.skillPoolsByCategory
  : {};

const PASSIVE_SKILL_OPTIONS_RAW = Array.isArray(generatedCatalog?.skills?.passive)
  ? generatedCatalog.skills.passive
  : [];
const ACTIVE_SKILL_OPTIONS_RAW = Array.isArray(generatedCatalog?.skills?.active)
  ? generatedCatalog.skills.active
  : [];

const SKILL_OPTIONS_BY_ID = new Map();

const normalizeSkillOption = (entry = {}) => {
  const skillId = Number(entry?.skillId || 0);
  const parsedSkillId = Number.isFinite(skillId) ? Math.trunc(skillId) : 0;
  const baseLabel = String(entry?.label || "").trim();
  const payload = {
    skillId: parsedSkillId > 0 ? parsedSkillId : 0,
    label: baseLabel || (parsedSkillId > 0 ? `Skill ${parsedSkillId}` : "Unknown Skill"),
    description: String(entry?.description || ""),
    source: String(entry?.source || "unknown"),
    skillType: Number.isFinite(Number(entry?.skillType))
      ? Math.trunc(Number(entry.skillType))
      : null,
    effectIds: Array.isArray(entry?.effectIds)
      ? entry.effectIds.map((value) => Number(value)).filter((value) => Number.isFinite(value)).map((value) => Math.trunc(value))
      : [],
    effectValues: Array.isArray(entry?.effectValues)
      ? entry.effectValues.map((value) => Number(value)).filter((value) => Number.isFinite(value))
      : [],
    effectFactor: Number.isFinite(Number(entry?.effectFactor)) ? Number(entry.effectFactor) : 1,
    isPercent: Boolean(entry?.isPercent),
    maxLevel: Math.max(1, Math.floor(Number(entry?.maxLevel || 1))),
    upgradeCosts: Array.isArray(entry?.upgradeCosts)
      ? entry.upgradeCosts.map((value) => Math.max(0, Math.floor(Number(value || 0))))
      : [],
  };
  if (payload.skillId > 0) {
    SKILL_OPTIONS_BY_ID.set(payload.skillId, payload);
  }
  return payload;
};

export const OFFICER_PASSIVE_SKILL_OPTIONS = PASSIVE_SKILL_OPTIONS_RAW
  .map(normalizeSkillOption)
  .filter((entry) => entry.skillId > 0);

export const OFFICER_ACTIVE_SKILL_OPTIONS = ACTIVE_SKILL_OPTIONS_RAW
  .map(normalizeSkillOption)
  .filter((entry) => entry.skillId > 0);

// Backward-compatible alias used by existing table renderers.
export const OFFICER_SKILL_OPTIONS = OFFICER_PASSIVE_SKILL_OPTIONS;

const OFFICER_UNWORLDLY_GRADE_BY_TAG = Object.freeze({
  0: "",
  1: "A",
  2: "AA",
  3: "S",
  4: "SS",
  5: "SSS",
});

const OFFICER_UNWORLDLY_COLOR_BY_RANK = Object.freeze({
  2: "legacy",
  3: "blue",
  4: "purple",
  5: "orange",
});

const OFFICER_UNWORLDLY_ORDER_BY_RANK_TAG = Object.freeze({
  "2:0": 0,
  "3:0": 1,
  "4:1": 2,
  "4:2": 3,
  "5:3": 4,
  "5:4": 5,
  "5:5": 6,
});

const buildUnworldlyTierMeta = (tierRank = 0, tierTag = 0) => {
  const rank = Number.isFinite(Number(tierRank)) ? Math.trunc(Number(tierRank)) : 0;
  const tag = Number.isFinite(Number(tierTag)) ? Math.max(0, Math.trunc(Number(tierTag))) : 0;
  const grade = OFFICER_UNWORLDLY_GRADE_BY_TAG[tag] ?? "";
  const color = OFFICER_UNWORLDLY_COLOR_BY_RANK[rank] || "unknown";
  const isLegacyTier = rank <= 2;
  const displayLabel = isLegacyTier
    ? "Legacy"
    : `${String(color || "").replace(/^./, (ch) => ch.toUpperCase())}${grade ? ` ${grade}` : ""}`.trim();
  const orderKey = `${rank}:${tag}`;
  const order = Number.isFinite(Number(OFFICER_UNWORLDLY_ORDER_BY_RANK_TAG[orderKey]))
    ? Math.trunc(Number(OFFICER_UNWORLDLY_ORDER_BY_RANK_TAG[orderKey]))
    : Math.max(0, (rank * 10) + tag);
  return {
    tierTag: tag,
    tierGrade: grade,
    tierColorFamily: color,
    tierDisplayLabel: displayLabel,
    tierOrder: order,
    isLegacyTier,
  };
};

const normalizeTier = (raw = {}) => {
  const tierId = Number.isFinite(Number(raw?.tierId)) ? Math.trunc(Number(raw.tierId)) : null;
  const tierRank = Number.isFinite(Number(raw?.tierRank)) ? Math.trunc(Number(raw.tierRank)) : null;
  const tierTag = Number.isFinite(Number(raw?.tierTag))
    ? Math.max(0, Math.trunc(Number(raw.tierTag)))
    : Number.isFinite(Number(raw?.tag))
      ? Math.max(0, Math.trunc(Number(raw.tag)))
      : 0;
  const meta = buildUnworldlyTierMeta(tierRank, tierTag);
  const tierDisplayLabel = String(raw?.tierDisplayLabel || raw?.tierLabel || meta.tierDisplayLabel || "").trim();
  return {
    tierId,
    tierRank,
    tierTag,
    tierGrade: String(raw?.tierGrade || meta.tierGrade || ""),
    tierColorFamily: String(raw?.tierColorFamily || meta.tierColorFamily || ""),
    tierOrder: Number.isFinite(Number(raw?.tierOrder)) ? Math.max(0, Math.trunc(Number(raw.tierOrder))) : meta.tierOrder,
    tierDisplayLabel: tierDisplayLabel || meta.tierDisplayLabel,
    tierLabel: tierDisplayLabel || meta.tierDisplayLabel,
    isLegacyTier: Boolean(raw?.isLegacyTier ?? meta.isLegacyTier),
    maxLevel: Math.max(1, Math.floor(Number(raw?.maxLevel || 1))),
    effectValues: Array.isArray(raw?.effectValues)
      ? raw.effectValues.map((value) => Number(value)).filter((value) => Number.isFinite(value))
      : [],
    powerValues: Array.isArray(raw?.powerValues)
      ? raw.powerValues.map((value) => Number(value)).filter((value) => Number.isFinite(value))
      : [],
    weight: Number.isFinite(Number(raw?.weight)) ? Math.trunc(Number(raw.weight)) : 0,
  };
};

const normalizeUnworldlyOption = (raw = {}) => ({
  groupId: Number.isFinite(Number(raw?.groupId)) ? Math.trunc(Number(raw.groupId)) : null,
  skillId: Number.isFinite(Number(raw?.skillId)) ? Math.trunc(Number(raw.skillId)) : null,
  label: String(raw?.label || ""),
  description: String(raw?.description || ""),
  tiers: Array.isArray(raw?.tiers)
    ? raw.tiers
      .map(normalizeTier)
      .sort((left, right) => (
        Number(left?.tierOrder || 0) - Number(right?.tierOrder || 0)
        || Number(left?.tierRank || 0) - Number(right?.tierRank || 0)
        || Number(left?.tierId || 0) - Number(right?.tierId || 0)
      ))
    : [],
});

const normalizeStarStageRow = (raw = {}) => ({
  star: Number.isFinite(Number(raw?.star)) ? Math.trunc(Number(raw.star)) : 0,
  maxStage: Math.max(0, Math.floor(Number(raw?.maxStage || 0))),
  masteryValues: Array.isArray(raw?.masteryValues)
    ? raw.masteryValues.map((value) => Number(value)).filter((value) => Number.isFinite(value)).map((value) => Math.trunc(value))
    : [],
  upgradeCost: Math.max(0, Math.floor(Number(raw?.upgradeCost || 0))),
});

const OFFICER_SKIN_TIER_FALLBACK_MAX = 9;

const toSortedUniqueIntList = (rawValues = []) => (
  Array.isArray(rawValues)
    ? [...new Set(
      rawValues
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))
        .map((value) => Math.trunc(value))
        .filter((value) => value >= 0),
    )].sort((left, right) => left - right)
    : []
);

const buildSequentialIntRange = (minValue, maxValue) => {
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue) || maxValue < minValue) return [];
  const out = [];
  for (let value = minValue; value <= maxValue; value += 1) {
    out.push(value);
  }
  return out;
};

const normalizeOfficerSkinTierMeta = (entry = {}, icon = {}, skinIconPath = "") => {
  const explicitTierOptions = toSortedUniqueIntList(entry?.skinTierOptions);
  const explicitMinTier = Number(entry?.skinTierMin);
  const explicitMaxTier = Number(entry?.skinTierMax);
  const skinIconGids = toSortedUniqueIntList(icon?.skinIconGids);
  const hasSkinRows = Array.isArray(entry?.skins) && entry.skins.length > 0;
  const hasSkin = Boolean(skinIconPath) || skinIconGids.length > 0 || hasSkinRows;
  if (!hasSkin) {
    return {
      hasSkin: false,
      skinTierMin: null,
      skinTierMax: null,
      skinTierOptions: [],
    };
  }

  const resolvedMinTier = Number.isFinite(explicitMinTier)
    ? Math.max(0, Math.trunc(explicitMinTier))
    : (explicitTierOptions.length ? explicitTierOptions[0] : 0);
  const resolvedMaxTier = Number.isFinite(explicitMaxTier)
    ? Math.max(resolvedMinTier, Math.trunc(explicitMaxTier))
    : (explicitTierOptions.length
      ? explicitTierOptions[explicitTierOptions.length - 1]
      : OFFICER_SKIN_TIER_FALLBACK_MAX);
  const tierOptions = explicitTierOptions.length
    ? explicitTierOptions.filter((value) => value >= resolvedMinTier && value <= resolvedMaxTier)
    : buildSequentialIntRange(resolvedMinTier, resolvedMaxTier);

  return {
    hasSkin: true,
    skinTierMin: resolvedMinTier,
    skinTierMax: resolvedMaxTier,
    skinTierOptions: tierOptions,
  };
};

const normalizeOfficerSkinEffectRows = (rawRows = []) => (
  Array.isArray(rawRows)
    ? rawRows
      .map((row = {}) => {
        const effectId = Number(row?.effectId);
        const value = Number(row?.value);
        if (!Number.isFinite(effectId) || !Number.isFinite(value) || effectId <= 0) return null;
        return {
          effectId: Math.trunc(effectId),
          value: Math.trunc(value),
        };
      })
      .filter(Boolean)
    : []
);

const normalizeOfficerSkinRows = (rawSkins = []) => (
  Array.isArray(rawSkins)
    ? rawSkins
      .map((skin = {}) => {
        const skinGid = Number(skin?.skinGid);
        if (!Number.isFinite(skinGid) || skinGid <= 0) return null;
        const rankRows = Array.isArray(skin?.rankRows)
          ? skin.rankRows
            .map((row = {}) => {
              const rank = Number(row?.rank);
              if (!Number.isFinite(rank) || rank < 0) return null;
              return {
                rank: Math.trunc(rank),
                power: Math.max(0, Math.trunc(Number(row?.power || 0))),
                upgradeItemCount: Math.max(0, Math.trunc(Number(row?.upgradeItemCount || 0))),
                primaryEffects: normalizeOfficerSkinEffectRows(row?.primaryEffects),
                milestoneEffects: normalizeOfficerSkinEffectRows(row?.milestoneEffects),
                effectDeltas: normalizeOfficerSkinEffectRows(row?.effectDeltas),
                effectIcon: String(row?.effectIcon || ""),
                nameToken: String(row?.nameToken || ""),
                textToken: String(row?.textToken || ""),
                unlockTextToken: String(row?.unlockTextToken || ""),
                isMilestone: Boolean(row?.isMilestone),
              };
            })
            .filter(Boolean)
            .sort((left, right) => left.rank - right.rank)
          : [];
        return {
          skinGid: Math.trunc(skinGid),
          skinItemGid: Number.isFinite(Number(skin?.skinItemGid)) ? Math.trunc(Number(skin.skinItemGid)) : null,
          rankRows,
        };
      })
      .filter(Boolean)
    : []
);

const normalizeOfficerCatalogEntry = (entry = {}) => {
  const officerId = Number(entry?.officerId || 0);
  const parsedOfficerId = Number.isFinite(officerId) ? Math.trunc(officerId) : 0;
  const categoryRaw = String(entry?.category || "").trim().toLowerCase();
  const category = OFFICER_CATEGORY_ORDER.includes(categoryRaw) ? categoryRaw : "warfare";
  const label = String(entry?.label || entry?.name || "").trim();
  const skillPoolSkillIds = Array.isArray(entry?.skillPoolSkillIds)
    ? entry.skillPoolSkillIds.map((value) => Number(value)).filter((value) => Number.isFinite(value)).map((value) => Math.trunc(value))
    : [];
  const fixedPassiveSkillSlotIds = (() => {
    const raw = Array.isArray(entry?.fixedPassiveSkillSlotIds) ? entry.fixedPassiveSkillSlotIds : [];
    const normalized = [0, 1, 2].map((idx) => {
      const parsed = Number(raw[idx]);
      if (!Number.isFinite(parsed)) return null;
      const value = Math.trunc(parsed);
      return value > 0 ? value : null;
    });
    return normalized;
  })();
  const passiveSkillSelectionMode = (() => {
    const raw = String(entry?.passiveSkillSelectionMode || "").trim().toLowerCase();
    if (raw === "fixed" || raw === "pool") return raw;
    return fixedPassiveSkillSlotIds.some((value) => Number(value || 0) > 0) ? "fixed" : "pool";
  })();
  const starStages = Array.isArray(entry?.starStages) ? entry.starStages.map(normalizeStarStageRow) : [];
  const unworldlySkillOptions = Array.isArray(entry?.unworldlySkillOptions)
    ? entry.unworldlySkillOptions.map(normalizeUnworldlyOption)
    : [];
  const icon = entry?.icon && typeof entry.icon === "object" ? entry.icon : {};
  const iconPath = String(entry?.iconPath || icon?.baseIconPath || "").trim();
  const skinIconPath = String(entry?.skinIconPath || icon?.defaultSkinIconPath || "").trim();
  const skinTierMeta = normalizeOfficerSkinTierMeta(entry, icon, skinIconPath);
  const skins = normalizeOfficerSkinRows(entry?.skins);
  const equipmentIds = Array.isArray(entry?.equipmentIds)
    ? entry.equipmentIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value))
      .map((value) => Math.trunc(value))
      .filter((value) => value > 0)
    : [];
  const equipmentSlots = Array.isArray(entry?.equipmentSlots)
    ? entry.equipmentSlots
      .map((slot = {}) => ({
        slotIndex: Number.isFinite(Number(slot?.slotIndex)) ? Math.max(1, Math.trunc(Number(slot.slotIndex))) : 1,
        equipId: Number.isFinite(Number(slot?.equipId)) ? Math.trunc(Number(slot.equipId)) : null,
        label: String(slot?.label || ""),
        maxLevel: Number.isFinite(Number(slot?.maxLevel)) ? Math.max(0, Math.trunc(Number(slot.maxLevel))) : 0,
        masteryTrackIds: Array.isArray(slot?.masteryTrackIds)
          ? slot.masteryTrackIds
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value))
            .map((value) => Math.trunc(value))
            .filter((value) => value > 0)
          : [],
      }))
      .filter((slot) => Number(slot?.equipId || 0) > 0)
      .sort((left, right) => left.slotIndex - right.slotIndex)
    : [];
  const resolvedEquipmentIds = equipmentIds.length
    ? equipmentIds
    : equipmentSlots.map((slot) => Number(slot?.equipId || 0)).filter((value) => value > 0);
  return {
    officerId: parsedOfficerId > 0 ? parsedOfficerId : 0,
    category,
    categoryLabel: String(entry?.categoryLabel || OFFICER_CATEGORY_LABELS[category] || "Warfare"),
    label: label || (parsedOfficerId > 0 ? `Officer ${parsedOfficerId}` : "Officer"),
    name: String(entry?.name || label || ""),
    description: String(entry?.description || ""),
    tier: String(entry?.tier || "unknown"),
    quality: Math.max(0, Math.floor(Number(entry?.quality || 0))),
    type: Number.isFinite(Number(entry?.type)) ? Math.trunc(Number(entry.type)) : null,
    maxStar: Math.max(0, Math.floor(Number(entry?.maxStar || 6))),
    maxLevel: Math.max(0, Math.floor(Number(entry?.maxLevel || OFFICER_CONSTRAINTS?.officerMaxLevel || 55))),
    fragmentItemGid: Number.isFinite(Number(entry?.fragmentItemGid)) ? Math.trunc(Number(entry.fragmentItemGid)) : null,
    fragmentItemLabel: String(entry?.fragmentItemLabel || ""),
    fragmentFuseCount: Math.max(0, Math.floor(Number(entry?.fragmentFuseCount || 0))),
    baseSkillId: Number.isFinite(Number(entry?.baseSkillId)) ? Math.trunc(Number(entry.baseSkillId)) : null,
    breakthroughSkillId: Number.isFinite(Number(entry?.breakthroughSkillId)) ? Math.trunc(Number(entry.breakthroughSkillId)) : null,
    skillPoolType: Number.isFinite(Number(entry?.skillPoolType)) ? Math.trunc(Number(entry.skillPoolType)) : null,
    skillPoolSkillIds,
    passiveSkillSelectionMode,
    fixedPassiveSkillSlotIds,
    starStages,
    unworldlySkillOptions,
    specialSkillPoolGroups: Array.isArray(entry?.specialSkillPoolGroups)
      ? entry.specialSkillPoolGroups.map((value) => Number(value)).filter((value) => Number.isFinite(value)).map((value) => Math.trunc(value))
      : [],
    equipmentUnlock: Boolean(entry?.equipmentUnlock),
    equipmentIds: resolvedEquipmentIds,
    equipmentSlots,
    hasSkin: skinTierMeta.hasSkin,
    skinTierMin: skinTierMeta.skinTierMin,
    skinTierMax: skinTierMeta.skinTierMax,
    skinTierOptions: skinTierMeta.skinTierOptions,
    skins,
    iconPath: iconPath || null,
    skinIconPath: skinIconPath || null,
    icon: {
      ...icon,
      baseIconPath: iconPath || null,
      defaultSkinIconPath: skinIconPath || null,
    },
  };
};

export const OFFICER_BASE_ROWS = (
  Array.isArray(generatedCatalog?.officers) ? generatedCatalog.officers : []
)
  .map(normalizeOfficerCatalogEntry)
  .filter((entry) => entry.officerId > 0)
  .sort((a, b) => {
    const categoryDelta = OFFICER_CATEGORY_ORDER.indexOf(a.category) - OFFICER_CATEGORY_ORDER.indexOf(b.category);
    if (categoryDelta !== 0) return categoryDelta;
    return Number(a.officerId || 0) - Number(b.officerId || 0);
  });

const OFFICER_BASE_ROW_BY_ID = OFFICER_BASE_ROWS.reduce((acc, entry) => {
  acc[String(entry.officerId)] = entry;
  return acc;
}, {});

const SKILL_MAX_BY_STAR = Array.isArray(OFFICER_CONSTRAINTS?.skillMaxByStar)
  ? OFFICER_CONSTRAINTS.skillMaxByStar.map((value) => Math.max(0, Math.floor(Number(value || 0))))
  : [4, 9, 14, 19, 19, 19];
const SKILL_SLOT_UNLOCK_STARS = Array.isArray(OFFICER_CONSTRAINTS?.skillSlotUnlockStars)
  ? OFFICER_CONSTRAINTS.skillSlotUnlockStars.map((value) => Math.max(0, Math.floor(Number(value || 0))))
  : [2, 3, 4];
const UNWORLDLY_SLOT_UNLOCK_DECIMAL_STARS = Array.isArray(OFFICER_CONSTRAINTS?.unworldlySlotUnlockStars)
  ? OFFICER_CONSTRAINTS.unworldlySlotUnlockStars
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 0)
  : [5.1, 5.2, 5.3, 5.4, 6.0];
const UNWORLDLY_FIRST_SLOT_UNLOCK_STARS = 5.1;
const EQUIPMENT_SLOT_UNLOCK_DECIMAL_STARS = Array.isArray(OFFICER_CONSTRAINTS?.equipmentSlotUnlockStars)
  ? OFFICER_CONSTRAINTS.equipmentSlotUnlockStars
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 0)
  : [3.0, 4.0];
const OFFICER_MAX_LEVEL = Math.max(0, Math.floor(Number(OFFICER_CONSTRAINTS?.officerMaxLevel || 55)));

const normalizeXpItem = (raw = {}) => {
  const itemGid = Number.isFinite(Number(raw?.itemGid)) ? Math.trunc(Number(raw.itemGid)) : 0;
  const xp = Math.max(0, Math.floor(Number(raw?.xp || 0)));
  if (itemGid <= 0 || xp <= 0) return null;
  return {
    itemGid,
    level: Math.max(0, Math.floor(Number(raw?.level || 0))),
    xp,
    label: String(raw?.label || `Item #${itemGid}`),
  };
};

export const OFFICER_RESOURCE_CATALOG = Object.freeze({
  officerXpItems: Array.isArray(RESOURCE_CATALOG_RAW?.officerXpItems)
    ? RESOURCE_CATALOG_RAW.officerXpItems.map(normalizeXpItem).filter(Boolean).sort((left, right) => left.xp - right.xp)
    : [],
  officerSkillXpItems: Array.isArray(RESOURCE_CATALOG_RAW?.officerSkillXpItems)
    ? RESOURCE_CATALOG_RAW.officerSkillXpItems.map(normalizeXpItem).filter(Boolean).sort((left, right) => left.xp - right.xp)
    : [],
});

const parseNumeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeEquipmentEffectPair = (raw = {}) => {
  const effectId = Number.isFinite(Number(raw?.effectId)) ? Math.trunc(Number(raw.effectId)) : 0;
  if (effectId <= 0) return null;
  return {
    effectId,
    value: Number.isFinite(Number(raw?.value)) ? Math.trunc(Number(raw.value)) : 0,
  };
};

const normalizeMaterialCostRow = (raw = {}) => {
  const itemGid = Number.isFinite(Number(raw?.itemGid)) ? Math.trunc(Number(raw.itemGid)) : 0;
  const count = Math.max(0, Math.floor(Number(raw?.count || 0)));
  if (itemGid <= 0 || count <= 0) return null;
  return {
    resourceType: String(raw?.resourceType || "item"),
    itemGid,
    level: Math.max(0, Math.floor(Number(raw?.level || 0))),
    count,
    label: String(raw?.label || `Item #${itemGid}`),
  };
};

const normalizeEquipmentEffectBucket = (raw = {}) => ({
  bucketIndex: Number.isFinite(Number(raw?.bucketIndex)) ? Math.max(1, Math.trunc(Number(raw.bucketIndex))) : 1,
  descTextId: String(raw?.descTextId || "").trim() || null,
  descText: String(raw?.descText || ""),
  effects: Array.isArray(raw?.effects)
    ? raw.effects.map(normalizeEquipmentEffectPair).filter(Boolean)
    : [],
});

const normalizeEquipmentMasteryTrack = (raw = {}) => {
  const masteryId = Number.isFinite(Number(raw?.masteryId)) ? Math.trunc(Number(raw.masteryId)) : 0;
  if (masteryId <= 0) return null;
  const levelRows = Array.isArray(raw?.levelRows)
    ? raw.levelRows
      .map((row = {}) => ({
        level: Number.isFinite(Number(row?.level)) ? Math.max(1, Math.trunc(Number(row.level))) : 1,
        power: Number.isFinite(Number(row?.power)) ? Math.max(0, Math.trunc(Number(row.power))) : 0,
        effects: Array.isArray(row?.effects) ? row.effects.map(normalizeEquipmentEffectPair).filter(Boolean) : [],
        costs: Array.isArray(row?.costs) ? row.costs.map(normalizeMaterialCostRow).filter(Boolean) : [],
      }))
      .sort((left, right) => left.level - right.level)
    : [];
  return {
    masteryId,
    label: String(raw?.label || `Mastery #${masteryId}`),
    description: String(raw?.description || ""),
    unlockLevel: Number.isFinite(Number(raw?.unlockLevel)) ? Math.max(1, Math.trunc(Number(raw.unlockLevel))) : null,
    maxLevel: Number.isFinite(Number(raw?.maxLevel))
      ? Math.max(0, Math.trunc(Number(raw.maxLevel)))
      : levelRows.reduce((max, row) => Math.max(max, Number(row?.level || 0)), 0),
    levelRows,
  };
};

const normalizeEquipmentLevelRow = (raw = {}) => ({
  level: Number.isFinite(Number(raw?.level)) ? Math.max(1, Math.trunc(Number(raw.level))) : 1,
  power: Number.isFinite(Number(raw?.power)) ? Math.max(0, Math.trunc(Number(raw.power))) : 0,
  rank: Number.isFinite(Number(raw?.rank)) ? Math.max(0, Math.trunc(Number(raw.rank))) : 0,
  masteryIds: Array.isArray(raw?.masteryIds)
    ? raw.masteryIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value))
      .map((value) => Math.trunc(value))
      .filter((value) => value > 0)
    : [],
  masteryLevelMax: Number.isFinite(Number(raw?.masteryLevelMax)) ? Math.max(0, Math.trunc(Number(raw.masteryLevelMax))) : 0,
  effectBuckets: Array.isArray(raw?.effectBuckets) ? raw.effectBuckets.map(normalizeEquipmentEffectBucket) : [],
  costs: Array.isArray(raw?.costs) ? raw.costs.map(normalizeMaterialCostRow).filter(Boolean) : [],
});

const normalizeEquipmentCatalogEntry = (raw = {}) => {
  const equipId = Number.isFinite(Number(raw?.equipId)) ? Math.trunc(Number(raw.equipId)) : 0;
  if (equipId <= 0) return null;
  const masteryTracks = Array.isArray(raw?.masteryTracks)
    ? raw.masteryTracks.map(normalizeEquipmentMasteryTrack).filter(Boolean)
    : [];
  const levelRows = Array.isArray(raw?.levelRows)
    ? raw.levelRows.map(normalizeEquipmentLevelRow).sort((left, right) => left.level - right.level)
    : [];
  return {
    equipId,
    label: String(raw?.label || `Equipment #${equipId}`),
    description: String(raw?.description || ""),
    iconGid: Number.isFinite(Number(raw?.iconGid)) ? Math.trunc(Number(raw.iconGid)) : null,
    iconSprite: String(raw?.iconSprite || "").trim() || null,
    iconPath: String(raw?.iconPath || "").trim() || null,
    maxLevel: Number.isFinite(Number(raw?.maxLevel))
      ? Math.max(0, Math.trunc(Number(raw.maxLevel)))
      : levelRows.reduce((max, row) => Math.max(max, Number(row?.level || 0)), 0),
    firstMasteryUnlockLevel: Number.isFinite(Number(raw?.firstMasteryUnlockLevel))
      ? Math.max(1, Math.trunc(Number(raw.firstMasteryUnlockLevel)))
      : null,
    secondMasteryUnlockLevel: Number.isFinite(Number(raw?.secondMasteryUnlockLevel))
      ? Math.max(1, Math.trunc(Number(raw.secondMasteryUnlockLevel)))
      : null,
    masteryTrackIds: Array.isArray(raw?.masteryTrackIds)
      ? raw.masteryTrackIds
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value))
        .map((value) => Math.trunc(value))
        .filter((value) => value > 0)
      : masteryTracks.map((track) => track.masteryId),
    masteryTracks,
    levelRows,
  };
};

const OFFICER_EQUIPMENT_CATALOG_RAW = generatedCatalog?.officerEquipmentCatalog
  && typeof generatedCatalog.officerEquipmentCatalog === "object"
  ? generatedCatalog.officerEquipmentCatalog
  : {};

const OFFICER_EQUIPMENT_CATALOG = Array.isArray(OFFICER_EQUIPMENT_CATALOG_RAW?.equipments)
  ? OFFICER_EQUIPMENT_CATALOG_RAW.equipments
    .map(normalizeEquipmentCatalogEntry)
    .filter(Boolean)
    .sort((left, right) => left.equipId - right.equipId)
  : [];

const OFFICER_EQUIPMENT_BY_ID = OFFICER_EQUIPMENT_CATALOG.reduce((acc, entry) => {
  acc.set(entry.equipId, entry);
  return acc;
}, new Map());

export const decimalStarToStarStage = (rawStars = 0, officerId = null) => {
  const officer = officerId ? getOfficerCatalogEntryById(officerId) : null;
  const maxStar = Math.max(0, Math.floor(Number(officer?.maxStar || 6)));
  const bounded = Math.max(0, Math.min(rawStars, maxStar + 0.4));
  let baseStar = Math.max(0, Math.floor(bounded));
  let stage = Math.round((bounded - baseStar) * 10);
  if (stage < 0) stage = 0;
  if (stage > 4) {
    // UI keeps decimal stars, but values x.5..x.9 carry to the next full star.
    baseStar += 1;
    stage = 0;
  }
  if (baseStar > maxStar) {
    baseStar = maxStar;
    stage = 0;
  }
  const maxStageByStar = OFFICER_CONSTRAINTS?.decimalStarEncoding?.maxStagePerStar || {};
  const starMaxStage = Math.max(
    0,
    Math.floor(Number(maxStageByStar?.[String(baseStar)] ?? (baseStar >= maxStar ? 0 : 4))),
  );
  let normalizedStage = Math.min(stage, starMaxStage);
  if (normalizedStage < stage && stage > 4 && baseStar < maxStar) {
    baseStar += 1;
    normalizedStage = 0;
  }
  if (baseStar > maxStar) {
    baseStar = maxStar;
    normalizedStage = 0;
  }
  const normalizedStar = Math.min(baseStar, maxStar);
  const normalizedDecimal = normalizedStar + (normalizedStage / 10);
  return {
    stars: Math.round(normalizedDecimal * 10) / 10,
    star: normalizedStar,
    stage: normalizedStage,
    maxStage: starMaxStage,
  };
};

export const starStageToDecimalStar = (star = 0, stage = 0) => {
  const normalizedStar = Math.max(0, Math.floor(parseNumeric(star, 0)));
  const normalizedStage = Math.max(0, Math.min(4, Math.floor(parseNumeric(stage, 0))));
  return Math.round((normalizedStar + (normalizedStage / 10)) * 10) / 10;
};

export const getOfficerUnworldlySlotUnlockStar = (slotIndex) => {
  const parsed = Number(slotIndex);
  if (!Number.isFinite(parsed)) return Number.MAX_SAFE_INTEGER;
  const idx = Math.max(0, Math.floor(parsed));
  if (idx === 0) return UNWORLDLY_FIRST_SLOT_UNLOCK_STARS;
  const fallback = UNWORLDLY_SLOT_UNLOCK_DECIMAL_STARS[UNWORLDLY_SLOT_UNLOCK_DECIMAL_STARS.length - 1] ?? 6.0;
  return Number(UNWORLDLY_SLOT_UNLOCK_DECIMAL_STARS[idx] ?? fallback);
};

export const isOfficerUnworldlySlotUnlocked = (decimalStars = 0, slotIndex = 0, officerId = null) => {
  const normalizedStars = decimalStarToStarStage(decimalStars, officerId).stars;
  const threshold = getOfficerUnworldlySlotUnlockStar(slotIndex);
  return normalizedStars + 1e-9 >= threshold;
};

export const getOfficerEquipmentSlotUnlockStar = (slotIndex) => {
  const parsed = Number(slotIndex);
  if (!Number.isFinite(parsed)) return Number.MAX_SAFE_INTEGER;
  const idx = Math.max(0, Math.floor(parsed));
  const fallback = EQUIPMENT_SLOT_UNLOCK_DECIMAL_STARS[EQUIPMENT_SLOT_UNLOCK_DECIMAL_STARS.length - 1] ?? 4.0;
  return Number(EQUIPMENT_SLOT_UNLOCK_DECIMAL_STARS[idx] ?? fallback);
};

export const isOfficerEquipmentSlotUnlocked = (decimalStars = 0, slotIndex = 0, officerId = null) => {
  const normalizedStars = decimalStarToStarStage(decimalStars, officerId).stars;
  const threshold = getOfficerEquipmentSlotUnlockStar(slotIndex);
  return normalizedStars + 1e-9 >= threshold;
};

export const getOfficerCategoryLabel = (key) => OFFICER_CATEGORY_LABELS[String(key || "").toLowerCase()] || "Warfare";

export const getOfficerCatalogEntryById = (officerId) => {
  const parsed = Number(officerId);
  if (!Number.isFinite(parsed)) return null;
  return OFFICER_BASE_ROW_BY_ID[String(Math.trunc(parsed))] || null;
};

const OFFICER_ICON_ASSET_PREFIX = "/battle-simulation/officer-icons";

const buildOfficerIconAssetPath = (...values) => {
  for (const value of values) {
    const normalized = String(value || "").trim();
    if (!normalized) continue;
    if (/^https?:\/\//i.test(normalized) || normalized.startsWith("/")) {
      return normalized;
    }
    if (/\.(png|jpe?g|webp|gif|svg)$/i.test(normalized)) {
      return `${OFFICER_ICON_ASSET_PREFIX}/${normalized.replace(/^\/+/, "")}`;
    }
    return `${OFFICER_ICON_ASSET_PREFIX}/${normalized}.png`;
  }
  return null;
};

export const getOfficerIconPathsById = (officerId) => {
  const officer = getOfficerCatalogEntryById(officerId);
  if (!officer) return { base: null, skin: null };
  const icon = officer.icon && typeof officer.icon === "object" ? officer.icon : {};
  return {
    base: buildOfficerIconAssetPath(
      officer.iconPath,
      icon.baseIconPath,
      icon.baseIconSprite,
      icon.baseIconGid ? `icon_${icon.baseIconGid}` : "",
    ),
    skin: buildOfficerIconAssetPath(
      officer.skinIconPath,
      icon.defaultSkinIconPath,
      icon.defaultSkinIconSprite,
      icon.defaultSkinIconGid ? `icon_${icon.defaultSkinIconGid}` : "",
    ),
  };
};

export const getOfficerDisplayIconPath = (officerId, preferSkin = false) => {
  const paths = getOfficerIconPathsById(officerId);
  if (preferSkin && paths.skin) return paths.skin;
  return paths.base || paths.skin || null;
};

export const getOfficerSkinTierBounds = (officerId) => {
  const officer = getOfficerCatalogEntryById(officerId);
  if (!officer?.hasSkin) {
    return {
      hasSkin: false,
      minTier: null,
      maxTier: null,
      tierOptions: [],
    };
  }
  const minTier = Math.max(0, Math.floor(Number(officer?.skinTierMin || 0)));
  const maxTier = Math.max(minTier, Math.floor(Number(officer?.skinTierMax ?? OFFICER_SKIN_TIER_FALLBACK_MAX)));
  const tierOptions = toSortedUniqueIntList(officer?.skinTierOptions);
  return {
    hasSkin: true,
    minTier,
    maxTier,
    tierOptions: tierOptions.length ? tierOptions : buildSequentialIntRange(minTier, maxTier),
  };
};

export const getOfficerSkinPowerContribution = (officerId, skinLevel = null) => {
  const officer = getOfficerCatalogEntryById(officerId);
  if (!officer?.hasSkin || !Array.isArray(officer?.skins) || !officer.skins.length) return 0;
  const parsedLevel = Number(skinLevel);
  if (!Number.isFinite(parsedLevel) || parsedLevel < 0) return 0;
  const targetRank = Math.trunc(parsedLevel);
  const rankRows = officer.skins
    .flatMap((skin) => (Array.isArray(skin?.rankRows) ? skin.rankRows : []))
    .filter((row) => Number.isFinite(Number(row?.rank)) && Number(row.rank) <= targetRank)
    .sort((left, right) => Number(left.rank) - Number(right.rank));
  if (!rankRows.length) return 0;
  return Math.max(0, Math.trunc(Number(rankRows[rankRows.length - 1]?.power || 0)));
};

const resolveOfficerEquipmentLevelRow = (levelRows = [], selectedLevel = 0) => {
  if (!Array.isArray(levelRows) || !levelRows.length) return null;
  const target = Math.max(1, Math.floor(parseNumeric(selectedLevel, 1)));
  return levelRows.find((row) => Number(row?.level || 0) === target)
    || [...levelRows].reverse().find((row) => Number(row?.level || 0) <= target)
    || levelRows[0];
};

const resolveOfficerEquipmentMasteryLevelRow = (track = null, selectedLevel = 0) => {
  if (!track || !Array.isArray(track?.levelRows) || !track.levelRows.length) return null;
  const target = Math.max(1, Math.floor(parseNumeric(selectedLevel, 1)));
  return track.levelRows.find((row) => Number(row?.level || 0) === target)
    || [...track.levelRows].reverse().find((row) => Number(row?.level || 0) <= target)
    || track.levelRows[0];
};

export const getOfficerEquipmentCatalogEntryById = (equipId) => {
  const parsedId = Number.isFinite(Number(equipId)) ? Math.trunc(Number(equipId)) : 0;
  if (parsedId <= 0) return null;
  return OFFICER_EQUIPMENT_BY_ID.get(parsedId) || null;
};

export const getOfficerEquipmentSlotsForOfficer = (officerId) => {
  const officer = getOfficerCatalogEntryById(officerId);
  if (!officer) return [];
  const ids = Array.isArray(officer?.equipmentIds)
    ? officer.equipmentIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value))
      .map((value) => Math.trunc(value))
      .filter((value) => value > 0)
    : [];
  if (!ids.length) return [];
  return ids.map((equipId, index) => {
    const catalogEntry = getOfficerEquipmentCatalogEntryById(equipId);
    const slotMeta = Array.isArray(officer?.equipmentSlots)
      ? officer.equipmentSlots.find((slot) => Number(slot?.equipId || 0) === equipId)
      : null;
    const masteryTracks = Array.isArray(catalogEntry?.masteryTracks)
      ? catalogEntry.masteryTracks
      : [];
    const masteryTrackIds = Array.isArray(catalogEntry?.masteryTrackIds)
      ? catalogEntry.masteryTrackIds
      : masteryTracks.map((track) => Number(track?.masteryId || 0)).filter((value) => value > 0);
    return {
      slotIndex: Number(slotMeta?.slotIndex || index + 1),
      equipId,
      label: String(catalogEntry?.label || slotMeta?.label || `Equipment #${equipId}`),
      iconPath: String(catalogEntry?.iconPath || slotMeta?.iconPath || "").trim() || null,
      maxLevel: Math.max(0, Number(catalogEntry?.maxLevel || slotMeta?.maxLevel || 0)),
      levelRows: Array.isArray(catalogEntry?.levelRows) ? catalogEntry.levelRows : [],
      masteryTracks,
      masteryTrackIds,
      firstMasteryUnlockLevel: Number.isFinite(Number(catalogEntry?.firstMasteryUnlockLevel))
        ? Math.trunc(Number(catalogEntry.firstMasteryUnlockLevel))
        : null,
      secondMasteryUnlockLevel: Number.isFinite(Number(catalogEntry?.secondMasteryUnlockLevel))
        ? Math.trunc(Number(catalogEntry.secondMasteryUnlockLevel))
        : null,
    };
  });
};

const normalizeOfficerEquipmentSlotSelection = (
  slotMeta = {},
  rawSlot = {},
  { decimalStars = 0, officerId = null } = {},
) => {
  const maxLevel = Math.max(0, Math.floor(parseNumeric(slotMeta?.maxLevel, 0)));
  const slotIndex = Math.max(0, Math.floor(parseNumeric(slotMeta?.slotIndex, 1)) - 1);
  const slotUnlocked = isOfficerEquipmentSlotUnlocked(decimalStars, slotIndex, officerId);
  const selectedLevel = slotUnlocked
    ? Math.max(0, Math.min(maxLevel, Math.floor(parseNumeric(rawSlot?.level, 0))))
    : 0;
  const selectedLevelRow = selectedLevel > 0
    ? resolveOfficerEquipmentLevelRow(slotMeta?.levelRows, selectedLevel)
    : null;
  const unlockedMasteryIds = new Set(
    Array.isArray(selectedLevelRow?.masteryIds)
      ? selectedLevelRow.masteryIds.map((value) => Number(value)).filter((value) => Number.isFinite(value)).map((value) => Math.trunc(value))
      : [],
  );
  const maxMasteryByParent = Math.max(0, Math.floor(parseNumeric(selectedLevelRow?.masteryLevelMax, 0)));
  const rawMasteryRows = Array.isArray(rawSlot?.masteryLevels)
    ? rawSlot.masteryLevels
    : [];
  const rawMasteryById = new Map(
    rawMasteryRows
      .map((row) => {
        const id = Number.isFinite(Number(row?.id)) ? Math.trunc(Number(row.id)) : 0;
        const level = Math.max(0, Math.floor(parseNumeric(row?.level, 0)));
        return [id, level];
      })
      .filter(([id]) => id > 0),
  );
  const masteryLevels = (Array.isArray(slotMeta?.masteryTracks) ? slotMeta.masteryTracks : [])
    .map((track) => {
      const masteryId = Number(track?.masteryId || 0);
      if (masteryId <= 0) return null;
      const trackMaxLevel = Math.max(0, Math.floor(parseNumeric(track?.maxLevel, 0)));
      const unlocked = unlockedMasteryIds.has(masteryId);
      const maxAllowed = unlocked ? Math.max(0, Math.min(maxMasteryByParent, trackMaxLevel)) : 0;
      const selected = Math.max(0, Math.floor(parseNumeric(rawMasteryById.get(masteryId), 0)));
      return {
        id: masteryId,
        level: Math.min(maxAllowed, selected),
      };
    })
    .filter(Boolean);
  return {
    equipId: Number(slotMeta?.equipId || null),
    level: selectedLevel,
    masteryLevels,
  };
};

export const normalizeOfficerEquipmentRowsForOfficer = (officerId, rawSlots = [], decimalStars = 0) => {
  const slots = getOfficerEquipmentSlotsForOfficer(officerId);
  if (!slots.length) return [];
  const sourceRows = Array.isArray(rawSlots) ? rawSlots : [];
  const normalizedStarValue = decimalStarToStarStage(decimalStars, officerId).stars;
  return slots.map((slotMeta, slotIndex) => {
    const byPosition = sourceRows[slotIndex];
    const byEquipId = sourceRows.find((entry) => Number(entry?.equipId || 0) === Number(slotMeta?.equipId || 0));
    const raw = (byPosition && typeof byPosition === "object" ? byPosition : null)
      || (byEquipId && typeof byEquipId === "object" ? byEquipId : {})
      || {};
    return normalizeOfficerEquipmentSlotSelection(slotMeta, raw, {
      decimalStars: normalizedStarValue,
      officerId,
    });
  });
};

export const getOfficerEquipmentEffectContributions = (officerId, rawSlots = [], decimalStars = 0) => {
  const slots = getOfficerEquipmentSlotsForOfficer(officerId);
  const normalizedSlots = normalizeOfficerEquipmentRowsForOfficer(officerId, rawSlots, decimalStars);
  if (!slots.length || !normalizedSlots.length) return [];
  const out = [];
  normalizedSlots.forEach((slot, idx) => {
    const slotMeta = slots[idx];
    if (!slotMeta || Number(slot?.level || 0) <= 0) return;
    const levelRow = resolveOfficerEquipmentLevelRow(slotMeta.levelRows, slot.level);
    if (levelRow) {
      const buckets = Array.isArray(levelRow?.effectBuckets) ? levelRow.effectBuckets : [];
      buckets.forEach((bucket) => {
        const effects = Array.isArray(bucket?.effects) ? bucket.effects : [];
        effects.forEach((effect) => {
          const effectId = Number(effect?.effectId || 0);
          const value = Math.round(parseNumeric(effect?.value, 0));
          if (effectId <= 0 || value === 0) return;
          out.push({
            source: "equipment",
            slotIndex: Number(slotMeta.slotIndex || idx + 1),
            equipId: Number(slotMeta.equipId || 0),
            equipLabel: String(slotMeta.label || `Equipment #${slotMeta.equipId}`),
            level: Number(slot.level || 0),
            effectId,
            value,
            descText: String(bucket?.descText || ""),
            bucketIndex: Number(bucket?.bucketIndex || 0),
          });
        });
      });
    }
    const masteryRows = Array.isArray(slot?.masteryLevels) ? slot.masteryLevels : [];
    masteryRows.forEach((mastery) => {
      const masteryId = Number(mastery?.id || 0);
      const masteryLevel = Number(mastery?.level || 0);
      if (masteryId <= 0 || masteryLevel <= 0) return;
      const track = Array.isArray(slotMeta?.masteryTracks)
        ? slotMeta.masteryTracks.find((entry) => Number(entry?.masteryId || 0) === masteryId)
        : null;
      const masteryLevelRow = resolveOfficerEquipmentMasteryLevelRow(track, masteryLevel);
      if (!masteryLevelRow) return;
      const effects = Array.isArray(masteryLevelRow?.effects) ? masteryLevelRow.effects : [];
      effects.forEach((effect) => {
        const effectId = Number(effect?.effectId || 0);
        const value = Math.round(parseNumeric(effect?.value, 0));
        if (effectId <= 0 || value === 0) return;
        out.push({
          source: "equipment_mastery",
          slotIndex: Number(slotMeta.slotIndex || idx + 1),
          equipId: Number(slotMeta.equipId || 0),
          equipLabel: String(slotMeta.label || `Equipment #${slotMeta.equipId}`),
          level: Number(slot.level || 0),
          masteryId,
          masteryLabel: String(track?.label || `Mastery #${masteryId}`),
          masteryLevel,
          effectId,
          value,
        });
      });
    });
  });
  return out;
};

export const getOfficerEquipmentPowerContribution = (officerId, rawSlots = [], decimalStars = 0) => {
  const slots = getOfficerEquipmentSlotsForOfficer(officerId);
  const normalizedSlots = normalizeOfficerEquipmentRowsForOfficer(officerId, rawSlots, decimalStars);
  if (!slots.length || !normalizedSlots.length) return 0;
  return normalizedSlots.reduce((sum, slot, idx) => {
    const slotMeta = slots[idx];
    if (!slotMeta || Number(slot?.level || 0) <= 0) return sum;
    const levelRow = resolveOfficerEquipmentLevelRow(slotMeta.levelRows, slot.level);
    const levelPower = Math.max(0, Math.floor(parseNumeric(levelRow?.power, 0)));
    const masteryPower = (Array.isArray(slot?.masteryLevels) ? slot.masteryLevels : []).reduce((inner, mastery) => {
      const masteryId = Number(mastery?.id || 0);
      const masteryLevel = Number(mastery?.level || 0);
      if (masteryId <= 0 || masteryLevel <= 0) return inner;
      const track = Array.isArray(slotMeta?.masteryTracks)
        ? slotMeta.masteryTracks.find((entry) => Number(entry?.masteryId || 0) === masteryId)
        : null;
      const levelTrackRow = resolveOfficerEquipmentMasteryLevelRow(track, masteryLevel);
      return inner + Math.max(0, Math.floor(parseNumeric(levelTrackRow?.power, 0)));
    }, 0);
    return sum + levelPower + masteryPower;
  }, 0);
};

export const getOfficerSkillOptionById = (skillId) => {
  const parsed = Number(skillId);
  if (!Number.isFinite(parsed)) return null;
  return SKILL_OPTIONS_BY_ID.get(Math.trunc(parsed)) || null;
};

export const getOfficerPassiveSkillPoolForCategory = (category) => {
  const key = String(category || "").trim().toLowerCase();
  const mapping = CATEGORY_POOL_BY_KEY?.[key];
  if (!mapping || !Array.isArray(mapping.skillIds)) return [];
  return mapping.skillIds
    .map((skillId) => getOfficerSkillOptionById(skillId))
    .filter(Boolean);
};

export const getOfficerPassiveSkillPoolForOfficer = (officerId) => {
  const officer = getOfficerCatalogEntryById(officerId);
  if (!officer) return [];
  if (Array.isArray(officer.skillPoolSkillIds) && officer.skillPoolSkillIds.length > 0) {
    return officer.skillPoolSkillIds.map((skillId) => getOfficerSkillOptionById(skillId)).filter(Boolean);
  }
  return getOfficerPassiveSkillPoolForCategory(officer.category);
};

export const getOfficerUnworldlyOptionsByOfficer = (officerId) => {
  const officer = getOfficerCatalogEntryById(officerId);
  return Array.isArray(officer?.unworldlySkillOptions) ? officer.unworldlySkillOptions : [];
};

export const resolveUnworldlyTierBySkill = (officerId, skillId, tierId = null) => {
  const options = getOfficerUnworldlyOptionsByOfficer(officerId);
  const parsedSkillId = Math.trunc(parseNumeric(skillId, 0));
  const parsedTierId = tierId !== null && tierId !== undefined ? Math.trunc(parseNumeric(tierId, 0)) : null;
  const option = options.find((entry) => Number(entry?.skillId || 0) === parsedSkillId);
  if (!option) return null;
  if (parsedTierId) {
    const tier = (option.tiers || []).find((entry) => Number(entry?.tierId || 0) === parsedTierId);
    if (tier) return { option, tier };
  }
  const visibleTiers = Array.isArray(option.tiers)
    ? option.tiers.filter((entry) => !Boolean(entry?.isLegacyTier))
    : [];
  const fallbackTier = visibleTiers[0]
    || (Array.isArray(option.tiers) && option.tiers.length ? option.tiers[0] : null);
  if (!fallbackTier) return null;
  return { option, tier: fallbackTier };
};

export const isOfficerSkillSlotUnlocked = (starInt, slotIndex) => {
  const threshold = SKILL_SLOT_UNLOCK_STARS[slotIndex] ?? Number.MAX_SAFE_INTEGER;
  return Math.floor(parseNumeric(starInt, 0)) >= threshold;
};

export const getOfficerSkillMaxLevelForStar = (starInt) => {
  const star = Math.max(0, Math.floor(parseNumeric(starInt, 0)));
  if (star <= 0) return 0;
  const idx = Math.min(star - 1, SKILL_MAX_BY_STAR.length - 1);
  return Math.max(0, Math.floor(parseNumeric(SKILL_MAX_BY_STAR[idx], 0)));
};

const resolveMasteryBaseByOfficer = (officer) => {
  const rank = Math.max(1, Math.floor(parseNumeric(officer?.quality, 1)));
  const isSealOfficer = Number(officer?.type) === 4 || Number(officer?.type) === 5;
  if (isSealOfficer) {
    if (rank >= 3) return OFFICER_MASTERY_TABLES?.sealOrange || OFFICER_MASTERY_TABLES?.orange || [];
    return OFFICER_MASTERY_TABLES?.sealPurple || OFFICER_MASTERY_TABLES?.purple || [];
  }
  if (rank >= 3) return OFFICER_MASTERY_TABLES?.orange || [];
  if (rank >= 2) return OFFICER_MASTERY_TABLES?.purple || [];
  return OFFICER_MASTERY_TABLES?.blue || [];
};

const resolveLevelExpTableByOfficer = (officer) => {
  const rank = Math.max(1, Math.floor(parseNumeric(officer?.quality, 1)));
  const isSealOfficer = Number(officer?.type) === 4 || Number(officer?.type) === 5;
  if (isSealOfficer) {
    if (rank >= 3) return OFFICER_LEVEL_EXP_TABLES?.sealOrange || OFFICER_LEVEL_EXP_TABLES?.orange || [];
    return OFFICER_LEVEL_EXP_TABLES?.sealPurple || OFFICER_LEVEL_EXP_TABLES?.purple || [];
  }
  if (rank >= 3) return OFFICER_LEVEL_EXP_TABLES?.orange || [];
  if (rank >= 2) return OFFICER_LEVEL_EXP_TABLES?.purple || [];
  return OFFICER_LEVEL_EXP_TABLES?.blue || [];
};

export const getOfficerLevelExpToNext = (officerId, internalLevel = 0) => {
  const officer = getOfficerCatalogEntryById(officerId);
  if (!officer) return 0;
  const level = Math.max(0, Math.floor(parseNumeric(internalLevel, 0)));
  const expTable = resolveLevelExpTableByOfficer(officer);
  return Math.max(0, Math.floor(parseNumeric(expTable?.[level], 0)));
};

export const getOfficerSkillLevelExpToNext = (internalLevel = 0) => {
  const level = Math.max(0, Math.floor(parseNumeric(internalLevel, 0)));
  const row = OFFICER_SKILL_LEVEL_EXP_TABLE.find((entry) => Math.floor(Number(entry?.level || 0)) === level);
  return Math.max(0, Math.floor(parseNumeric(row?.exp, 0)));
};

export const getOfficerMasteryValue = (officerId, level, decimalStars) => {
  const officer = getOfficerCatalogEntryById(officerId);
  if (!officer) return 0;
  const boundedLevel = Math.max(0, Math.min(OFFICER_MAX_LEVEL, Math.floor(parseNumeric(level, 0))));
  const starInfo = decimalStarToStarStage(decimalStars, officerId);
  const baseTable = resolveMasteryBaseByOfficer(officer);
  const baseValue = Number(baseTable?.[boundedLevel] || 0);
  const starRow = Array.isArray(officer.starStages)
    ? officer.starStages.find((entry) => Number(entry?.star || 0) === starInfo.star)
    : null;
  const growthValue = Number(starRow?.masteryValues?.[starInfo.stage] || 0);
  return Math.max(0, Math.floor(baseValue + growthValue));
};

const resolveSkillEffectValueAtLevel = (skill, level, effectIndex) => {
  const values = Array.isArray(skill?.effectValues) ? skill.effectValues : [];
  if (!values.length) return 0;
  const effectIds = Array.isArray(skill?.effectIds) ? skill.effectIds : [];
  const boundedLevelIndex = Math.max(0, Math.min(values.length - 1, level - 1));
  if (effectIds.length <= 1) {
    return Number(values[boundedLevelIndex] || 0);
  }
  const description = String(skill?.description || "");
  const placeholderIndexes = Array.from(description.matchAll(/\$(\d+)/g))
    .map((match) => Math.max(0, Math.floor(Number(match[1] || 0))));
  const highestPlaceholderIndex = placeholderIndexes.length ? Math.max(...placeholderIndexes) : 0;
  if (values.length === Math.max(1, Number(skill?.maxLevel || 1)) && highestPlaceholderIndex === 0) {
    return Number(values[boundedLevelIndex] || 0);
  }
  if (values.length >= effectIds.length) {
    return Number(values[Math.max(0, Math.min(values.length - 1, effectIndex))] || 0);
  }
  return Number(values[boundedLevelIndex] || 0);
};

const toTechValue = (skill, rawValue, masteryFactor) => {
  if (skill?.isPercent) {
    const scaledValue = rawValue * Number(skill?.effectFactor || 1) * masteryFactor;
    return Math.round(scaledValue * 10000) / 1000;
  }
  return Math.round(rawValue * masteryFactor * 1000) / 1000;
};

const formatOfficerPreviewValue = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0.000";
  return (Math.round(parsed * 1000) / 1000).toFixed(3);
};

export const computeOfficerSkillPreview = ({
  officerId,
  skillId,
  skillLevel = 1,
  officerLevel = 0,
  decimalStars = 0,
  applyMastery = true,
}) => {
  const skill = getOfficerSkillOptionById(skillId);
  if (!skill) return null;
  const mastery = getOfficerMasteryValue(officerId, officerLevel, decimalStars);
  const masteryFactor = applyMastery ? mastery * 0.01 : 1;
  const boundedLevel = Math.max(1, Math.min(skill.maxLevel || 1, Math.floor(parseNumeric(skillLevel, 1))));
  const rows = (skill.effectIds || []).map((effectId, effectIndex) => {
    const rawValue = resolveSkillEffectValueAtLevel(skill, boundedLevel, effectIndex);
    const scaledValue = rawValue * Number(skill.effectFactor || 1) * masteryFactor;
    return {
      effectId,
      rawValue,
      scaledValue,
      displayValue: skill.isPercent ? `${formatOfficerPreviewValue(scaledValue)}%` : formatOfficerPreviewValue(scaledValue),
      techValue: toTechValue(skill, rawValue, masteryFactor),
    };
  });
  return {
    skillId: skill.skillId,
    label: skill.label,
    description: skill.description,
    source: skill.source,
    level: boundedLevel,
    mastery,
    rows,
  };
};

export const createOfficerSeedRows = () => OFFICER_BASE_ROWS.map((entry) => ({
  officerId: entry.officerId,
  officerLabel: entry.label,
  category: entry.category,
  appointed: false,
  skinLevel: entry.hasSkin ? -1 : null,
  equipment: normalizeOfficerEquipmentRowsForOfficer(entry.officerId, []),
  level: 0,
  stars: 0,
  skills: [0, 1, 2].map((idx) => {
    const fixedSkillId = Number(entry?.fixedPassiveSkillSlotIds?.[idx] || 0);
    return {
      skillId: fixedSkillId > 0 ? fixedSkillId : null,
      level: 0,
    };
  }),
  breakthroughLevel: -1,
  unworldly: Array.from({ length: 5 }, () => ({
    groupId: null,
    skillId: null,
    tierId: null,
    tierRank: null,
    level: 0,
  })),
}));
